import { useEffect, useId, useRef, useState } from 'react'
import { userFacingMessage } from '../../api/errors'
import { useBuyCredits, type BillingStatus } from '../../api/queries/billing'
import { Button } from '../../ui/Button'
import { formatPrice } from './pricing'
import styles from './Billing.module.css'

/**
 * Choose a pack, then go to Stripe. The sizes and the unit price come from the
 * API, so the choices here are the ones the backend will accept and the price
 * shown is the one it will charge — neither is typed twice.
 */
export function BuyCreditsDialog({ pricing, onClose }: { pricing: BillingStatus['pricing']; onClose: () => void }) {
  const [quantity, setQuantity] = useState(pricing.credit_pack_sizes[0] ?? 0)
  const buy = useBuyCredits()
  const titleId = useId()
  const firstRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    firstRef.current?.focus()
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !buy.isPending) {
        event.stopImmediatePropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose, buy.isPending])

  function proceed() {
    buy.mutate(quantity, { onSuccess: ({ url }) => window.location.assign(url) })
  }

  return (
    <>
      <div className={styles.overlay} aria-hidden="true" onClick={buy.isPending ? undefined : onClose} />
      <div className={styles.overlay} style={{ background: 'transparent' }}>
        <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <h2 id={titleId} className={styles.dialogTitle}>Buy credits</h2>
          <p className={styles.dialogBody}>
            {formatPrice(pricing.credit_price_cents, pricing.currency)} per analysis, ex VAT. Purchased credits never expire.
          </p>
          <fieldset className={styles.packs}>
            <legend className="dp-sr-only">Pack size</legend>
            {pricing.credit_pack_sizes.map((size, i) => (
              <label key={size} className={styles.pack}>
                <input
                  ref={i === 0 ? firstRef : undefined}
                  type="radio"
                  name="pack"
                  value={size}
                  checked={quantity === size}
                  onChange={() => setQuantity(size)}
                  disabled={buy.isPending}
                />
                {size} credits
                <span className={styles.packPrice}>{formatPrice(size * pricing.credit_price_cents, pricing.currency)}</span>
              </label>
            ))}
          </fieldset>
          {buy.isError ? <p className={styles.error} role="alert">{userFacingMessage(buy.error)}</p> : null}
          <div className={styles.actions}>
            <Button variant="primary" onClick={proceed} disabled={buy.isPending || quantity <= 0}>
              {buy.isPending ? 'Opening Stripe…' : 'Continue to payment'}
            </Button>
            <Button onClick={onClose} disabled={buy.isPending}>Cancel</Button>
          </div>
        </div>
      </div>
    </>
  )
}
